import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCity } from "../context/CityContext";

import createPlusIcon from "../ui-assets/home/create-event-plus.svg";
import searchicon from "../ui-assets/home/search.svg";
import detecticon from "../ui-assets/home/detect-icon.svg";
import locationIcon from "../ui-assets/home/location.svg";
import { API_BASE_URL } from "../config";

const Navbar = ({ user, onSignOut }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

 const isFreeRegistrationPage =
  location.pathname.startsWith("/free-event-register/");

const isAllEventsPage =
  location.pathname === "/all-event";

  const speakerform =
  location.pathname.startsWith("/speaker-form/");

  const eventpage =
  location.pathname.startsWith("/eventpage/");

  const AttendeeManagementPage =
  location.pathname.startsWith("/attendee-management/");

  const SpeakerManagementPage =
  location.pathname.startsWith("/speaker-management/");

   const guidelines =
  location.pathname.startsWith("/guidelines/");

   const Scheduler =
  location.pathname.startsWith("/scheduler/");




  const [isOpen, setIsOpen] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileSubmitted, setProfileSubmitted] = useState(false);

  const { selectedCity, setSelectedCity } = useCity();

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showAllCities, setShowAllCities] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const [showToast, setShowToast] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [eventResults, setEventResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const modalRef = useRef(null);
  const popupRef = useRef(null);

  const handleSignOut = () => {
    onSignOut();
    setShowProfilePopup(false);
    navigate("/");
  };

  // Load profile from localStorage on mount
  useEffect(() => {
    const loadSavedProfile = () => {
      const savedProfile = localStorage.getItem('userProfile');
      if (savedProfile) {
        try {
          const profileData = JSON.parse(savedProfile);
          setUserData(profileData);
          console.log('Loaded profile from localStorage:', profileData);
        } catch (error) {
          console.error('Error parsing saved profile:', error);
        }
      }
    };

    loadSavedProfile();
  }, []);

  // Listen for profile submission events
  useEffect(() => {
    const handleProfileSubmit = (event) => {
      if (event.detail && event.detail.profileData) {
        setUserData(event.detail.profileData);
        setProfileSubmitted(true);
        console.log('Profile data received in navbar:', event.detail.profileData);
        // Auto-hide the highlight after 3 seconds
        setTimeout(() => setProfileSubmitted(false), 3000);
      }
    };

    window.addEventListener('profileSubmitted', handleProfileSubmit);

    return () => {
      window.removeEventListener('profileSubmitted', handleProfileSubmit);
    };
  }, []);

  // Fetch user profile data from API
  useEffect(() => {
    const fetchUserProfile = async () => {
     if (!user || isFreeRegistrationPage || isAllEventsPage || speakerform || eventpage || AttendeeManagementPage || SpeakerManagementPage || guidelines || Scheduler) {
  return;
}

      // Check if we already have data in userData state
      if (userData && Object.keys(userData).length > 0) {
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found");
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/user/profile`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: user.id || user.user_id
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.status === "success" && data?.data) {
          setUserData(data.data);
          // Save to localStorage
          localStorage.setItem('userProfile', JSON.stringify(data.data));
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user,isFreeRegistrationPage, isAllEventsPage,speakerform,eventpage,AttendeeManagementPage,SpeakerManagementPage,guidelines,Scheduler]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setShowProfilePopup(false);
      }
    };
    if (showProfilePopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfilePopup]);

  useEffect(() => {
    if (!showLocationModal) return;

    fetch(`${API_BASE_URL}/getMetroCities`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === "success") setCities(data.data);
      })
      .catch((err) => console.error("City API error:", err));
  }, [showLocationModal]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setShowLocationModal(false);
      }
    };
    if (showLocationModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLocationModal]);

  useEffect(() => {
    if (!citySearch.trim()) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      fetch(
        `${API_BASE_URL}/getCities?searchbyCity=${citySearch}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data?.status === "success") setSearchResults(data.data);
        })
        .catch((err) => console.error("City search error:", err));
    }, 100);
    return () => clearTimeout(timeout);
  }, [citySearch]);

  const handleSelectCity = (cityName) => {
    setSelectedCity(cityName);
    setShowLocationModal(false);
    setCitySearch("");
    setSearchResults([]);
    setShowAllCities(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleAllCities = () => {
    setSelectedCity("");
    setShowLocationModal(false);
    setCitySearch("");
    setSearchResults([]);
    setShowAllCities(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleViewAllCities = () => {
    if (!showAllCities && allCities.length === 0) {
      fetch(`${API_BASE_URL}/getCities`)
        .then((res) => res.json())
        .then((data) => {
          if (data?.status === "success") setAllCities(data.data);
        })
        .catch((err) => console.error("All cities error:", err));
    }
    setShowAllCities((prev) => !prev);
  };

  const cityDisplayLabel = selectedCity || "All Cities";

  // Get user email for popup (from API or user prop)
  const getUserEmail = () => {
    if (userData?.email) {
      return userData.email;
    }
    if (user?.email) {
      return user.email;
    }
    return "user@example.com";
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (userData?.user_profile) {
      return userData.user_profile;
    }
    if (userData?.name) {
      return userData.name;
    }
    if (user?.name) {
      return user.name;
    }
    return "User";
  };

  // Get user bio
  const getUserBio = () => {
    if (userData?.user_bio) {
      return userData.user_bio;
    }
    return null;
  };

  // Get user organization
  const getUserOrganization = () => {
    if (userData?.organization) {
      return userData.organization;
    }
    return null;
  };

  // Get user designation
  const getUserDesignation = () => {
    if (userData?.designation) {
      return userData.designation;
    }
    return null;
  };

  // Get user ID



  // Get profile image
  const getProfileImage = () => {
    if (userData?.profile_image) {
      return userData.profile_image;
    }
    return null;
  };

  // Get user initial for profile icon
  const getUserInitial = () => {
    const name = getUserDisplayName();
    if (name && name !== "User") {
      return name.charAt(0).toUpperCase();
    }
    const email = getUserEmail();
    if (email && email !== "user@example.com") {
      return email.charAt(0).toUpperCase();
    }
    return "U";
  };


  const handleDetectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log("Latitude:", position.coords.latitude);
          console.log("Longitude:", position.coords.longitude);
          alert(`Location detected: ${position.coords.latitude}, ${position.coords.longitude}`);
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Location detection failed. Please allow location access.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  useEffect(() => {
    if (!searchText.trim()) {
      setEventResults([]);
      setShowSearchDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);

        const city = selectedCity || "all";

        const res = await fetch(
          `${API_BASE_URL}/search_events?q=${encodeURIComponent(
            searchText
          )}&city=${encodeURIComponent(city)}`
        );

        const data = await res.json();

        setEventResults(data.result || []);
        setShowSearchDropdown(true);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, selectedCity]);















  return (
    <>
      <nav className="navbar">
        <div className="container">
          <div className="logo-search-wrapper">
            <Link to="/" className="logo" onClick={() => setIsOpen(false)}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <img
                  // src="/eventfork_logo.svg"
                  src="/eventfork_logo.svg"
                  alt="EventFork Logo"
                  width="200"
                  height="auto"
                  style={{ borderRadius: "6px", display: "block" }}
                />
                <div className="eventfork-logo"></div>
              </div>
            </Link>

            {isHomePage && (
              <div
                className="select-wrapper"
                onClick={() => setShowLocationModal(true)}
              >
                <div className="form-select home-city-select">
                  <span>{cityDisplayLabel}</span>
                </div>
              </div>
            )}

            <button
              className={`hamburger ${isOpen ? "is-active" : ""}`}
              aria-label="Toggle menu"
              aria-controls="primary-navigation"
              aria-expanded={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            >
              <span className="hamburger-box">
                <span className="hamburger-inner"></span>
              </span>
            </button>

            



          </div>





          <div
            id="primary-navigation"
            className={`nav-links ${isOpen ? "open" : ""}`}
          >

            <div
              className="dashboard-search-container"
              style={{ position: "relative" }}
            >
              <i className="fas fa-search"></i>

              <input
                type="text"
                placeholder="Search events..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />

              {showSearchDropdown && (
                <div className="search-dropdown">

                  {searchLoading ? (

                    <div className="search-item">
                      Searching...
                    </div>

                  ) : eventResults.length > 0 ? (

                    eventResults.map((event) => (
                      <div
                        key={event.metadata.event_id}
                        className="search-item"
                        onClick={() => {
                          setIsOpen(false);              // Close hamburger
                          setSearchText("");
                          setShowSearchDropdown(false);
                          navigate(`/eventpage/${event.metadata.event_id}`);
                        }}
                      >
                        <h5>{event.metadata.name}</h5>
                        <p>{event.metadata.city}</p>
                      </div>
                    ))

                  ) : (

                    <div className="search-item">
                      No events found
                    </div>

                  )}

                </div>
              )}
            </div>
            <Link
              to="/"
              className="home__link"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                  Dashboard
                </Link>
                <Link
                  to="/create-event"
                  onClick={() => setIsOpen(false)}
                  className="create-event"
                >
                   Create Event
                </Link>

                {/* <input
                type="text"
                placeholder="Search events..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              /> */}


                {/* Profile Icon - Replaces Logout Button */}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setShowProfilePopup(true);
                  }}
                  className="profile-icon-btn"
                  style={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginLeft: "0.5rem"
                  }}
                >
                  <div className={`profile-initial ${profileSubmitted ? 'profile-updated' : ''}`}>
                    {getProfileImage() ? (
                      <img
                        src={getProfileImage()}
                        alt="Profile"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      getUserInitial()
                    )}
                  </div>
                </button>
              </>
            ) : (
              <>
                <Link to="/demo" className="demo-tab">
                  Demo
                  <span className="demo-badge">New</span>
                </Link>
                <Link
                  to="/signin"
                  className="btn btn-secondary"
                  onClick={() => setIsOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="btn btn-primary"
                  onClick={() => setIsOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>











        </div>
      </nav>

      {/* Profile Popup - Slides from Right */}
      {showProfilePopup && (
        <>
          <div
            className="profile-dropdown-overlay"
            onClick={() => setShowProfilePopup(false)}
          ></div>

          <div
            className="profile-dropdown slide-from-right"
            ref={popupRef}
          >
            {/* Profile Section */}
            <div className="dropdown-section">
              <div
                className="compact-profile"
                onClick={() => {
                  window.location.href = '/dashboard#settings';
                  setShowProfilePopup(false);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="compact-avatar">
                  {getProfileImage() ? (
                    <img
                      src={getProfileImage()}
                      alt="Profile"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <span className="compact-initial">
                      {getUserInitial()}
                    </span>
                  )}
                </div>

                <div className="compact-info">
                  <p className="compact-name">
                    {getUserDisplayName()}
                  </p>

                  <p className="compact-email">
                    {getUserEmail()}
                  </p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="dropdown-divider"></div>

            {/* Logout Button */}
            <div className="dropdown-section">
              <button
                className="dropdown-logout-btn"
                onClick={handleSignOut}
              >
                <span className="logout-icon"></span>
                Log Out
              </button>
            </div>
          </div>
        </>
      )}







      {/* Location Modal */}
      {showLocationModal && (
        <div className="location-backdrop">
          <div className="modal-wrapper slide-top" ref={modalRef}>
            <div className="container">
              <div className="input-wrapper" style={{ position: "relative" }}>
                <img src={searchicon} alt="search icon" />
                <input
                  className="form-control search-location"
                  type="search"
                  placeholder="Search for your city"
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  autoFocus
                />

                {searchResults.length > 0 && (
                  <ul
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "white",
                      border: "1px solid #ddd",
                      borderRadius: "0px",
                      listStyle: "none",
                      padding: "0px",
                      margin: 0,
                      zIndex: 9999,
                      maxHeight: "470px",
                      overflowY: "auto",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      borderTop: "none",
                      borderBottom: "none",
                    }}
                  >
                    {searchResults.map((city, idx) => {
                      const query = citySearch;
                      const name = city.city_name;
                      const matchIdx = name
                        .toLowerCase()
                        .indexOf(query.toLowerCase());
                      const isFirst = idx === 0;

                      return (
                        <li
                          key={city.id}
                          onClick={() => handleSelectCity(city.city_name)}
                          style={{
                            cursor: "pointer",
                            padding: "10px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                            borderBottom: "1px solid #dee2e6",
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f5f5f5")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <span>
                            <img
                              src={locationIcon}
                              alt="location-icon"
                              style={{ position: "static" }}
                            />
                          </span>
                          <span>
                            {matchIdx >= 0 ? (
                              <>
                                {name.substring(0, matchIdx)}
                                <strong>
                                  {name.substring(
                                    matchIdx,
                                    matchIdx + query.length,
                                  )}
                                </strong>
                                {isFirst ? (
                                  <span>
                                    {name.substring(matchIdx + query.length)}
                                  </span>
                                ) : (
                                  name.substring(matchIdx + query.length)
                                )}
                              </>
                            ) : (
                              name
                            )}
                            <p
                              style={{
                                color: "#1a202c",
                                fontSize: "0.82em",
                                margin: "0px",
                              }}
                            >
                              {city.state_name}
                            </p>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="detect-wrap">
                <p className="detect-location" onClick={handleDetectLocation}>
                  <img src={detecticon} alt="detect icon" />
                  <span>Detect my location</span>
                </p>
                <p className="all-cities" onClick={handleAllCities}>
                  All Cities
                </p>
              </div>
            </div>

            <div className="popular-cities">
              <h4>Popular Cities</h4>
              <ul>
                {cities.map((city) => (
                  <li
                    key={city.id}
                    onClick={() => handleSelectCity(city.name)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={city.imagesrc} alt={city.name} />
                    <p>{city.name}</p>
                  </li>
                ))}
              </ul>
            </div>

            <button className="view-all-btn" onClick={handleViewAllCities}>
              {showAllCities ? "Hide All Cities" : "View All Cities"}
            </button>

            {showAllCities && (
              <div className="all-city-wrap">
                <p className="all-cities-title">
                  <i className="fas fa-building"></i> Other Cities
                </p>

                <div className="all-city-grid">
                  {allCities.map((city) => (
                    <div
                      key={city.id}
                      onClick={() => handleSelectCity(city.city_name)}
                      className="city-item"
                    >
                      <i className="fas fa-city"></i>
                      <span>{city.city_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}












      {/* Toast notification */}
      {showToast && (
        <>
          <style>{`
            @keyframes toastFadeInOut {
              0%   { opacity: 0; transform: translateX(-50%) translateY(10px); }
              15%  { opacity: 1; transform: translateX(-50%) translateY(0); }
              75%  { opacity: 1; transform: translateX(-50%) translateY(0); }
              100% { opacity: 0; transform: translateX(-50%) translateY(10px); }
            }
          `}</style>

          <div
            style={{
              position: "fixed",
              top: "90px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "#9a1a04ff",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "14px",
              fontWeight: 500,
              zIndex: 99999,
              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              animation: "toastFadeInOut 3s ease forwards",
              whiteSpace: "nowrap",
            }}
          >
            <span className="location-toast">
              <img src={locationIcon} alt="location-icon" />
            </span>
            {selectedCity
              ? `Your location is updated to ${selectedCity}.`
              : "Showing events from all cities."}
          </div>



        </>
      )}
    </>
  );
};

export default Navbar;